import { useState, useCallback, useEffect } from "react";
import ERPLayout from "@/components/feature/ERPLayout";
import { adminApi } from "@/services/api";

const mockSystemStats = {
  dbSize: "145 MB",
  storageUsed: "2.4 GB",
  storageTotal: "10 GB",
  storagePercent: 24,
};

// ── API Diagnostics Panel ─────────────────────────────────────────────────────
const API_BASE_URL = (import.meta.env.VITE_API_URL as string) || '/api';

interface DiagResult {
  step: string;
  status: 'ok' | 'fail' | 'warn';
  detail: string;
}

function ApiDiagnosticsPanel() {
  const [running, setRunning] = useState(false);
  const [results, setResults] = useState<DiagResult[]>([]);
  const [rawError, setRawError] = useState<string | null>(null);
  const [done, setDone] = useState(false);

  const runDiagnostics = async () => {
    setRunning(true);
    setResults([]);
    setRawError(null);
    setDone(false);
    const out: DiagResult[] = [];

    // Step 1: Check VITE_API_URL
    const apiUrl = API_BASE_URL;
    if (!apiUrl || apiUrl === '/api') {
      out.push({ step: 'VITE_API_URL', status: 'warn', detail: `Using relative /api — OK for same-origin, but may fail if frontend and backend are on different origins. Current value: "${apiUrl}"` });
    } else {
      out.push({ step: 'VITE_API_URL', status: 'ok', detail: `Set to: ${apiUrl}` });
    }
    setResults([...out]);

    // Step 2: Ping /health (no auth)
    const baseUrl = apiUrl.replace(/\/api$/, '');
    const healthUrl = `${baseUrl}/health`;
    try {
      const start = Date.now();
      const res = await fetch(healthUrl, { method: 'GET', signal: AbortSignal.timeout(8000) });
      const ms = Date.now() - start;
      if (res.ok) {
        const data = await res.json();
        out.push({ step: 'Health Endpoint (/health)', status: 'ok', detail: `HTTP 200 in ${ms}ms — ${JSON.stringify(data)}` });
      } else {
        out.push({ step: 'Health Endpoint (/health)', status: 'fail', detail: `HTTP ${res.status} ${res.statusText} in ${ms}ms` });
      }
    } catch (err: unknown) {
      const msg = err instanceof Error ? err.message : String(err);
      out.push({ step: 'Health Endpoint (/health)', status: 'fail', detail: `Network error: ${msg}` });
      setRawError(msg);
    }
    setResults([...out]);

    // Step 3: Ping /api/auth/me (with auth token)
    const token = localStorage.getItem('token');
    const meUrl = `${apiUrl}/auth/me`;
    try {
      const start = Date.now();
      const res = await fetch(meUrl, {
        method: 'GET',
        headers: { Authorization: `Bearer ${token ?? ''}`, 'Content-Type': 'application/json' },
        signal: AbortSignal.timeout(8000),
      });
      const ms = Date.now() - start;
      if (res.ok) {
        out.push({ step: 'Auth API (/api/auth/me)', status: 'ok', detail: `HTTP 200 in ${ms}ms — token valid, auth working` });
      } else if (res.status === 401) {
        out.push({ step: 'Auth API (/api/auth/me)', status: 'warn', detail: `HTTP 401 in ${ms}ms — API reachable but token invalid/expired. Try logging out and back in.` });
      } else {
        out.push({ step: 'Auth API (/api/auth/me)', status: 'fail', detail: `HTTP ${res.status} ${res.statusText} in ${ms}ms` });
      }
    } catch (err: unknown) {
      const msg = err instanceof Error ? err.message : String(err);
      // Distinguish CORS from network
      if (msg.includes('Failed to fetch') || msg.includes('NetworkError') || msg.includes('CORS')) {
        out.push({ step: 'Auth API (/api/auth/me)', status: 'fail', detail: `CORS or network error: "${msg}". The backend is likely blocking this origin. Check FRONTEND_URL in backend .env and CORS config.` });
      } else {
        out.push({ step: 'Auth API (/api/auth/me)', status: 'fail', detail: `Network error: ${msg}` });
      }
      if (!rawError) setRawError(msg);
    }
    setResults([...out]);

    // Step 4: Check CORS headers via OPTIONS preflight
    try {
      const res = await fetch(`${apiUrl}/auth/me`, {
        method: 'OPTIONS',
        headers: { 'Origin': window.location.origin, 'Access-Control-Request-Method': 'GET', 'Access-Control-Request-Headers': 'Authorization' },
        signal: AbortSignal.timeout(5000),
      });
      const acao = res.headers.get('Access-Control-Allow-Origin');
      const acam = res.headers.get('Access-Control-Allow-Methods');
      if (acao) {
        const allowed = acao === '*' || acao === window.location.origin;
        out.push({
          step: 'CORS Preflight (OPTIONS)',
          status: allowed ? 'ok' : 'fail',
          detail: `Access-Control-Allow-Origin: "${acao}" | Methods: "${acam ?? '—'}" | Your origin: "${window.location.origin}" | ${allowed ? 'Origin is allowed ✓' : 'Origin NOT in allowed list — this is the CORS block!'}`,
        });
      } else {
        out.push({ step: 'CORS Preflight (OPTIONS)', status: 'warn', detail: `No CORS headers returned. Backend may not handle OPTIONS or is unreachable.` });
      }
    } catch (err: unknown) {
      const msg = err instanceof Error ? err.message : String(err);
      out.push({ step: 'CORS Preflight (OPTIONS)', status: 'fail', detail: `Preflight failed: ${msg}` });
    }
    setResults([...out]);

    // Step 5: Token presence
    if (!token) {
      out.push({ step: 'Auth Token', status: 'warn', detail: 'No token in localStorage — user may not be logged in. All authenticated API calls will return 401.' });
    } else {
      out.push({ step: 'Auth Token', status: 'ok', detail: `Token present (${token.length} chars). First 20: ${token.slice(0, 20)}...` });
    }
    setResults([...out]);

    setRunning(false);
    setDone(true);
  };

  const statusIcon = (s: DiagResult['status']) => {
    if (s === 'ok') return <i className="ri-checkbox-circle-fill text-amber-500" />;
    if (s === 'fail') return <i className="ri-close-circle-fill text-red-500" />;
    return <i className="ri-error-warning-fill text-amber-500" />;
  };

  const statusBg = (s: DiagResult['status']) => {
    if (s === 'ok') return 'bg-amber-50 border-amber-100';
    if (s === 'fail') return 'bg-red-50 border-red-100';
    return 'bg-amber-50 border-amber-100';
  };

  const statusText = (s: DiagResult['status']) => {
    if (s === 'ok') return 'text-amber-700';
    if (s === 'fail') return 'text-red-700';
    return 'text-amber-700';
  };

  const failCount = results.filter((r) => r.status === 'fail').length;
  const warnCount = results.filter((r) => r.status === 'warn').length;

  return (
    <div className="bg-white rounded-xl border-2 border-red-200 overflow-hidden">
      <div className="px-5 py-4 border-b border-red-100 flex items-center justify-between" style={{ background: '#fff5f5' }}>
        <div className="flex items-center gap-3">
          <div className="w-9 h-9 flex items-center justify-center rounded-xl bg-red-100 flex-shrink-0">
            <i className="ri-stethoscope-line text-base text-red-600" />
          </div>
          <div>
            <h3 className="font-bold text-gray-900 text-sm">API Connection Diagnostics</h3>
            <p className="text-xs text-gray-500 mt-0.5">
              Diagnose why the app is loading mock data instead of live database — checks CORS, auth, and network
            </p>
          </div>
        </div>
        <button
          onClick={runDiagnostics}
          disabled={running}
          className="flex items-center gap-2 px-4 py-2 bg-red-600 text-white rounded-xl text-sm font-bold cursor-pointer transition-all whitespace-nowrap disabled:opacity-60 hover:bg-red-700"
        >
          {running ? (
            <><i className="ri-loader-4-line animate-spin" />Running...</>
          ) : (
            <><i className="ri-bug-line" />Run Diagnostics</>
          )}
        </button>
      </div>

      <div className="p-5">
        {/* Current config summary */}
        <div className="mb-4 p-3 rounded-xl bg-gray-50 border border-gray-100">
          <p className="text-xs font-semibold text-gray-600 mb-2">Current Configuration</p>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 text-xs">
            <div className="flex items-center gap-2">
              <span className="text-gray-400 w-28 flex-shrink-0">VITE_API_URL:</span>
              <code className="font-mono bg-white border border-gray-200 px-2 py-0.5 rounded text-gray-800 truncate">{API_BASE_URL}</code>
            </div>
            <div className="flex items-center gap-2">
              <span className="text-gray-400 w-28 flex-shrink-0">Browser Origin:</span>
              <code className="font-mono bg-white border border-gray-200 px-2 py-0.5 rounded text-gray-800 truncate">{window.location.origin}</code>
            </div>
            <div className="flex items-center gap-2">
              <span className="text-gray-400 w-28 flex-shrink-0">Health URL:</span>
              <code className="font-mono bg-white border border-gray-200 px-2 py-0.5 rounded text-gray-800 truncate">{API_BASE_URL.replace(/\/api$/, '')}/health</code>
            </div>
            <div className="flex items-center gap-2">
              <span className="text-gray-400 w-28 flex-shrink-0">Token:</span>
              <code className="font-mono bg-white border border-gray-200 px-2 py-0.5 rounded text-gray-800">{localStorage.getItem('token') ? 'Present' : 'Missing'}</code>
            </div>
          </div>
        </div>

        {!done && !running && (
          <div className="flex flex-col items-center justify-center py-8 text-gray-400">
            <span className="w-14 h-14 flex items-center justify-center rounded-2xl bg-gray-50 mb-3">
              <i className="ri-bug-line text-3xl text-gray-300" />
            </span>
            <p className="text-sm font-medium text-gray-500">Click "Run Diagnostics" to find the issue</p>
            <p className="text-xs text-gray-400 mt-1">Tests CORS, auth token, network connectivity, and API reachability</p>
          </div>
        )}

        {running && results.length === 0 && (
          <div className="flex flex-col items-center justify-center py-8">
            <i className="ri-loader-4-line text-3xl animate-spin text-red-500 mb-3" />
            <p className="text-sm font-medium text-gray-600">Running diagnostics...</p>
          </div>
        )}

        {results.length > 0 && (
          <div className="space-y-2">
            {done && (
              <div className={`flex items-center gap-3 p-3 rounded-xl mb-3 ${failCount > 0 ? 'bg-red-50 border border-red-200' : warnCount > 0 ? 'bg-amber-50 border border-amber-200' : 'bg-amber-50 border border-amber-200'}`}>
                <i className={`text-xl ${failCount > 0 ? 'ri-close-circle-fill text-red-500' : warnCount > 0 ? 'ri-error-warning-fill text-amber-500' : 'ri-checkbox-circle-fill text-amber-500'}`} />
                <div>
                  <p className={`text-sm font-bold ${failCount > 0 ? 'text-red-700' : warnCount > 0 ? 'text-amber-700' : 'text-amber-700'}`}>
                    {failCount > 0
                      ? `${failCount} issue${failCount > 1 ? 's' : ''} found — this is why mock data is loading`
                      : warnCount > 0
                      ? `${warnCount} warning${warnCount > 1 ? 's' : ''} — check details below`
                      : 'All checks passed — API is reachable'}
                  </p>
                  {failCount > 0 && (
                    <p className="text-xs text-red-600 mt-0.5">Fix the failed steps below to enable live database writes</p>
                  )}
                </div>
              </div>
            )}

            {results.map((r, i) => (
              <div key={i} className={`flex items-start gap-3 p-3 rounded-xl border ${statusBg(r.status)}`}>
                <span className="w-5 h-5 flex items-center justify-center flex-shrink-0 mt-0.5">
                  {statusIcon(r.status)}
                </span>
                <div className="flex-1 min-w-0">
                  <p className={`text-xs font-bold ${statusText(r.status)}`}>{r.step}</p>
                  <p className={`text-xs mt-0.5 break-all leading-relaxed ${statusText(r.status)} opacity-80`}>{r.detail}</p>
                </div>
                {running && i === results.length - 1 && (
                  <i className="ri-loader-4-line animate-spin text-gray-400 flex-shrink-0" />
                )}
              </div>
            ))}

            {/* Fix guide */}
            {done && failCount > 0 && (
              <div className="mt-4 p-4 rounded-xl border border-red-200 bg-red-50">
                <p className="text-xs font-bold text-red-700 mb-2 flex items-center gap-1.5">
                  <i className="ri-tools-line" />
                  How to Fix
                </p>
                <ol className="text-xs text-red-600 space-y-1.5 list-decimal list-inside leading-relaxed">
                  <li>SSH into your server and check the backend is running: <code className="bg-red-100 px-1 rounded font-mono">pm2 status</code> or <code className="bg-red-100 px-1 rounded font-mono">systemctl status surveyerp</code></li>
                  <li>Open <code className="bg-red-100 px-1 rounded font-mono">backend/.env</code> and set <code className="bg-red-100 px-1 rounded font-mono">FRONTEND_URL={window.location.origin}</code></li>
                  <li>Restart the backend: <code className="bg-red-100 px-1 rounded font-mono">pm2 restart all</code></li>
                  <li>If using nginx, ensure <code className="bg-red-100 px-1 rounded font-mono">/api</code> is proxied to <code className="bg-red-100 px-1 rounded font-mono">localhost:5000</code></li>
                  <li>Check backend logs: <code className="bg-red-100 px-1 rounded font-mono">pm2 logs</code> — look for "CORS blocked origin" messages</li>
                </ol>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

interface BackupScheduleConfig {
  backup_enabled: boolean;
  backup_time: string;
  backup_retention_days: string;
  backup_destination: string;
  backup_compress: boolean;
  backup_notify_email: string;
}

interface DbTestResult {
  status: 'connected' | 'error';
  latency_ms: number;
  server_time?: string;
  db_version?: string;
  db_size_mb?: number;
  active_connections?: string;
  tables?: { table_name: string; table_rows: number; size_mb: number }[];
  error?: string;
}

interface BackupRecord {
  id: number;
  name: string;
  size: string;
  type: "Auto" | "Manual";
  status: "Success" | "Failed" | "In Progress";
  createdAt: string;
}

const mockBackups: BackupRecord[] = [
  { id: 1, name: "backup_2026-04-06_03-00-00.sql.gz", size: "145 MB", type: "Auto", status: "Success", createdAt: "2026-04-06T03:00:00" },
  { id: 2, name: "backup_2026-04-05_03-00-00.sql.gz", size: "143 MB", type: "Auto", status: "Success", createdAt: "2026-04-05T03:00:00" },
  { id: 3, name: "backup_2026-04-04_manual.sql.gz", size: "141 MB", type: "Manual", status: "Success", createdAt: "2026-04-04T14:30:00" },
  { id: 4, name: "backup_2026-04-04_03-00-00.sql.gz", size: "141 MB", type: "Auto", status: "Success", createdAt: "2026-04-04T03:00:00" },
  { id: 5, name: "backup_2026-04-03_03-00-00.sql.gz", size: "138 MB", type: "Auto", status: "Failed", createdAt: "2026-04-03T03:00:00" },
  { id: 6, name: "backup_2026-04-02_03-00-00.sql.gz", size: "136 MB", type: "Auto", status: "Success", createdAt: "2026-04-02T03:00:00" },
];

const DEFAULT_SCHEDULE: BackupScheduleConfig = {
  backup_enabled: true,
  backup_time: '03:00',
  backup_retention_days: '30',
  backup_destination: '/var/backups/surveyerp',
  backup_compress: true,
  backup_notify_email: '',
};

export default function AdminDatabasePage() {
  const [backups, setBackups] = useState<BackupRecord[]>(mockBackups);
  const [backingUp, setBackingUp] = useState(false);
  const [successMsg, setSuccessMsg] = useState("");
  const [dbTesting, setDbTesting] = useState(false);
  const [dbResult, setDbResult] = useState<DbTestResult | null>(null);
  const [dbTestTime, setDbTestTime] = useState<string | null>(null);

  // Backup schedule state
  const [schedule, setSchedule] = useState<BackupScheduleConfig>(DEFAULT_SCHEDULE);
  const [scheduleLoading, setScheduleLoading] = useState(true);
  const [scheduleSaving, setScheduleSaving] = useState(false);
  const [scheduleChanged, setScheduleChanged] = useState(false);

  const showSuccess = (msg: string) => { setSuccessMsg(msg); setTimeout(() => setSuccessMsg(""), 3000); };

  // Load backup schedule from backend on mount
  useEffect(() => {
    adminApi.getBackupSchedule()
      .then((data: unknown) => {
        const d = data as Record<string, string>;
        setSchedule({
          backup_enabled: d.backup_enabled !== 'false',
          backup_time: d.backup_time ?? '03:00',
          backup_retention_days: d.backup_retention_days ?? '30',
          backup_destination: d.backup_destination ?? '/var/backups/surveyerp',
          backup_compress: d.backup_compress !== 'false',
          backup_notify_email: d.backup_notify_email ?? '',
        });
      })
      .catch(() => {
        // API offline — keep defaults
      })
      .finally(() => setScheduleLoading(false));
  }, []);

  const updateSchedule = (patch: Partial<BackupScheduleConfig>) => {
    setSchedule((prev) => ({ ...prev, ...patch }));
    setScheduleChanged(true);
  };

  const handleSaveSchedule = useCallback(async () => {
    setScheduleSaving(true);
    try {
      await adminApi.saveBackupSchedule({
        backup_enabled: String(schedule.backup_enabled),
        backup_time: schedule.backup_time,
        backup_retention_days: schedule.backup_retention_days,
        backup_destination: schedule.backup_destination,
        backup_compress: String(schedule.backup_compress),
        backup_notify_email: schedule.backup_notify_email,
      });
      setScheduleChanged(false);
      showSuccess('Backup schedule saved to database');
    } catch (err: unknown) {
      const msg = err instanceof Error ? err.message : 'Failed to save schedule';
      showSuccess(`Error: ${msg}`);
    } finally {
      setScheduleSaving(false);
    }
  }, [schedule]);

  const handleDbTest = useCallback(async () => {
    setDbTesting(true);
    setDbResult(null);
    try {
      const result = await adminApi.testDbConnection() as DbTestResult;
      setDbResult(result);
      setDbTestTime(new Date().toLocaleTimeString("en-IN", { hour: "2-digit", minute: "2-digit", second: "2-digit", hour12: true }));
    } catch (err: unknown) {
      setDbResult({
        status: 'error',
        latency_ms: 0,
        error: err instanceof Error ? err.message : 'Connection failed — check server logs',
      });
      setDbTestTime(new Date().toLocaleTimeString("en-IN", { hour: "2-digit", minute: "2-digit", second: "2-digit", hour12: true }));
    } finally {
      setDbTesting(false);
    }
  }, []);

  const handleManualBackup = () => {
    setBackingUp(true);
    setTimeout(() => {
      const newBackup: BackupRecord = {
        id: Date.now(),
        name: `backup_${new Date().toISOString().replace(/[:.]/g, "-").slice(0, 19)}_manual.sql.gz`,
        size: "146 MB",
        type: "Manual",
        status: "Success",
        createdAt: new Date().toISOString(),
      };
      setBackups((prev) => [newBackup, ...prev]);
      setBackingUp(false);
      showSuccess("Manual backup completed successfully");
    }, 2500);
  };

  const tables = [
    { name: "job_cards", rows: 248, size: "12.4 MB" },
    { name: "clients", rows: 34, size: "2.1 MB" },
    { name: "invoices", rows: 92, size: "8.7 MB" },
    { name: "employees", rows: 10, size: "0.8 MB" },
    { name: "deliverables", rows: 45, size: "1.2 MB" },
    { name: "field_sessions", rows: 312, size: "18.5 MB" },
    { name: "notifications", rows: 1248, size: "4.3 MB" },
    { name: "audit_logs", rows: 4821, size: "22.1 MB" },
    { name: "users", rows: 9, size: "0.3 MB" },
    { name: "roles", rows: 6, size: "0.1 MB" },
  ];

  return (
    <ERPLayout title="Database & Backup" subtitle="Manage database health, backups, and storage">
      {successMsg && (
        <div className="fixed top-4 right-4 z-50 text-white text-sm px-4 py-2 rounded-xl font-medium" style={{ background: "#7c3aed" }}>
          <i className="ri-checkbox-circle-line mr-2" />{successMsg}
        </div>
      )}

      <div className="p-6 space-y-6">
        {/* Stats */}
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
          {[
            { label: "DB Size", value: mockSystemStats.dbSize, icon: "ri-database-2-line", color: "#7c3aed", bg: "#f5f3ff" },
            { label: "Storage Used", value: mockSystemStats.storageUsed, icon: "ri-hard-drive-2-line", color: "#ef4444", bg: "#fef2f2" },
            { label: "Total Backups", value: backups.length, icon: "ri-save-line", color: "#f59e0b", bg: "#fffbeb" },
            { label: "Last Backup", value: "3:00 AM", icon: "ri-time-line", color: "#f59e0b", bg: "#fffbeb" },
          ].map((s) => (
            <div key={s.label} className="bg-white rounded-xl p-4 border border-gray-100 flex items-center gap-3">
              <div className="w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0" style={{ background: s.bg }}>
                <i className={`${s.icon} text-lg`} style={{ color: s.color }} />
              </div>
              <div>
                <p className="text-xl font-bold text-gray-900">{s.value}</p>
                <p className="text-xs text-gray-500">{s.label}</p>
              </div>
            </div>
          ))}
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Storage */}
          <div className="bg-white rounded-xl border border-gray-100 p-5">
            <h3 className="font-bold text-gray-900 text-sm mb-4">Storage Overview</h3>
            <div className="mb-4">
              <div className="flex justify-between text-xs mb-1">
                <span className="text-gray-600">Used: {mockSystemStats.storageUsed}</span>
                <span className="text-gray-600">Total: {mockSystemStats.storageTotal}</span>
              </div>
              <div className="w-full bg-gray-100 rounded-full h-3">
                <div className="h-3 rounded-full" style={{ width: `${mockSystemStats.storagePercent}%`, background: "#7c3aed" }} />
              </div>
              <p className="text-xs text-gray-400 mt-1">{mockSystemStats.storagePercent}% used</p>
            </div>
            <div className="space-y-2">
              {[
                { label: "Field Photos", size: "1.1 GB", pct: 46, color: "#f59e0b" },
                { label: "Deliverables", size: "0.8 GB", pct: 33, color: "#7c3aed" },
                { label: "Database", size: "145 MB", pct: 6, color: "#10b981" },
                { label: "Backups", size: "350 MB", pct: 15, color: "#3b82f6" },
              ].map((item) => (
                <div key={item.label} className="flex items-center gap-3">
                  <span className="w-2 h-2 rounded-full flex-shrink-0" style={{ background: item.color }} />
                  <span className="text-xs text-gray-600 flex-1">{item.label}</span>
                  <span className="text-xs font-semibold text-gray-700">{item.size}</span>
                  <div className="w-20 bg-gray-100 rounded-full h-1.5">
                    <div className="h-1.5 rounded-full" style={{ width: `${item.pct}%`, background: item.color }} />
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Backup Controls */}
          <div className="bg-white rounded-xl border border-gray-100 p-5">
            <h3 className="font-bold text-gray-900 text-sm mb-4">Backup Controls</h3>
            <div className="space-y-3 mb-4">
              <div className="p-3 rounded-xl bg-amber-50 border border-amber-100">
                <div className="flex items-center gap-2 mb-1">
                  <span className="w-2 h-2 rounded-full bg-amber-500" />
                  <span className="text-xs font-semibold text-amber-700">Auto Backup: Enabled</span>
                </div>
                <p className="text-xs text-amber-600">Daily at 3:00 AM IST · Retention: 30 days</p>
              </div>
              <button
                onClick={handleManualBackup}
                disabled={backingUp}
                className="w-full flex items-center justify-center gap-2 text-white py-3 rounded-xl text-sm font-bold cursor-pointer transition-all whitespace-nowrap disabled:opacity-60"
                style={{ background: "#7c3aed" }}
              >
                {backingUp ? (
                  <><i className="ri-loader-4-line animate-spin" />Creating Backup...</>
                ) : (
                  <><i className="ri-save-line" />Create Manual Backup</>
                )}
              </button>
              <button onClick={() => showSuccess("Restore initiated — this may take a few minutes")}
                className="w-full flex items-center justify-center gap-2 border border-gray-200 text-gray-600 py-3 rounded-xl text-sm font-semibold cursor-pointer hover:bg-gray-50 transition-all whitespace-nowrap">
                <i className="ri-restart-line" />Restore from Backup
              </button>
            </div>
          </div>
        </div>

        {/* ── Scheduled Backup Configuration ──────────────────────────────── */}
        <div className="bg-white rounded-xl border border-gray-100 overflow-hidden">
          <div className="px-5 py-4 border-b border-gray-100 flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="w-9 h-9 flex items-center justify-center rounded-xl flex-shrink-0" style={{ background: '#f5f3ff' }}>
                <i className="ri-calendar-schedule-line text-base" style={{ color: '#7c3aed' }} />
              </div>
              <div>
                <h3 className="font-bold text-gray-900 text-sm">Scheduled Backup Configuration</h3>
                <p className="text-xs text-gray-500 mt-0.5">Set backup time, retention policy, and destination path</p>
              </div>
            </div>
            <div className="flex items-center gap-3">
              {scheduleChanged && (
                <span className="text-xs text-amber-600 font-medium flex items-center gap-1">
                  <i className="ri-error-warning-line" />
                  Unsaved changes
                </span>
              )}
              <button
                onClick={handleSaveSchedule}
                disabled={scheduleSaving || !scheduleChanged}
                className="flex items-center gap-2 px-4 py-2 text-white rounded-xl text-sm font-bold cursor-pointer transition-all whitespace-nowrap disabled:opacity-50"
                style={{ background: '#7c3aed' }}
              >
                {scheduleSaving ? (
                  <><i className="ri-loader-4-line animate-spin" />Saving...</>
                ) : (
                  <><i className="ri-save-line" />Save Schedule</>
                )}
              </button>
            </div>
          </div>

          {scheduleLoading ? (
            <div className="p-8 flex items-center justify-center gap-3 text-gray-400">
              <i className="ri-loader-4-line animate-spin text-xl" style={{ color: '#7c3aed' }} />
              <span className="text-sm">Loading backup configuration...</span>
            </div>
          ) : (
            <div className="p-5 space-y-5">
              {/* Enable / Disable toggle */}
              <div className="flex items-center justify-between p-4 rounded-xl border border-gray-100 bg-gray-50">
                <div className="flex items-center gap-3">
                  <span
                    className="w-9 h-9 flex items-center justify-center rounded-xl flex-shrink-0"
                    style={{ background: schedule.backup_enabled ? '#ecfdf5' : '#f3f4f6' }}
                  >
                    <i
                      className="ri-shield-check-line text-base"
                      style={{ color: schedule.backup_enabled ? '#10b981' : '#9ca3af' }}
                    />
                  </span>
                  <div>
                    <p className="text-sm font-semibold text-gray-800">Automatic Scheduled Backups</p>
                    <p className="text-xs text-gray-500">
                      {schedule.backup_enabled
                        ? `Enabled — runs daily at ${schedule.backup_time} IST`
                        : 'Disabled — no automatic backups will run'}
                    </p>
                  </div>
                </div>
                <button
                  type="button"
                  onClick={() => updateSchedule({ backup_enabled: !schedule.backup_enabled })}
                  className={`relative w-12 h-6 rounded-full transition-all cursor-pointer flex-shrink-0 ${
                    schedule.backup_enabled ? '' : 'bg-gray-200'
                  }`}
                  style={schedule.backup_enabled ? { background: '#10b981' } : {}}
                >
                  <span
                    className={`absolute top-0.5 w-5 h-5 bg-white rounded-full transition-all`}
                    style={{ left: schedule.backup_enabled ? '26px' : '2px' }}
                  />
                </button>
              </div>

              {/* Config grid */}
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                {/* Backup Time */}
                <div>
                  <label className="text-xs font-semibold text-gray-600 mb-1.5 block flex items-center gap-1.5">
                    <span className="w-4 h-4 flex items-center justify-center">
                      <i className="ri-time-line text-xs" style={{ color: '#7c3aed' }} />
                    </span>
                    Backup Time (24h)
                  </label>
                  <input
                    type="time"
                    value={schedule.backup_time}
                    onChange={(e) => updateSchedule({ backup_time: e.target.value })}
                    disabled={!schedule.backup_enabled}
                    className="w-full border border-gray-200 rounded-xl px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-violet-500/30 focus:border-violet-400 disabled:opacity-50 disabled:bg-gray-50 cursor-pointer"
                  />
                  <p className="text-xs text-gray-400 mt-1">Server local time (IST)</p>
                </div>

                {/* Retention Days */}
                <div>
                  <label className="text-xs font-semibold text-gray-600 mb-1.5 block flex items-center gap-1.5">
                    <span className="w-4 h-4 flex items-center justify-center">
                      <i className="ri-history-line text-xs" style={{ color: '#7c3aed' }} />
                    </span>
                    Retention Period
                  </label>
                  <div className="flex items-center gap-2">
                    <input
                      type="number"
                      min="1"
                      max="365"
                      value={schedule.backup_retention_days}
                      onChange={(e) => updateSchedule({ backup_retention_days: e.target.value })}
                      disabled={!schedule.backup_enabled}
                      className="flex-1 border border-gray-200 rounded-xl px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-violet-500/30 focus:border-violet-400 disabled:opacity-50 disabled:bg-gray-50"
                    />
                    <span className="text-sm text-gray-500 whitespace-nowrap">days</span>
                  </div>
                  <p className="text-xs text-gray-400 mt-1">Older backups auto-deleted</p>
                </div>

                {/* Notification Email */}
                <div>
                  <label className="text-xs font-semibold text-gray-600 mb-1.5 block flex items-center gap-1.5">
                    <span className="w-4 h-4 flex items-center justify-center">
                      <i className="ri-mail-line text-xs" style={{ color: '#7c3aed' }} />
                    </span>
                    Notify Email
                  </label>
                  <input
                    type="email"
                    value={schedule.backup_notify_email}
                    onChange={(e) => updateSchedule({ backup_notify_email: e.target.value })}
                    placeholder="admin@company.com"
                    disabled={!schedule.backup_enabled}
                    className="w-full border border-gray-200 rounded-xl px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-violet-500/30 focus:border-violet-400 disabled:opacity-50 disabled:bg-gray-50"
                  />
                  <p className="text-xs text-gray-400 mt-1">Alert on success or failure</p>
                </div>
              </div>

              {/* Destination Path */}
              <div>
                <label className="text-xs font-semibold text-gray-600 mb-1.5 block flex items-center gap-1.5">
                  <span className="w-4 h-4 flex items-center justify-center">
                    <i className="ri-folder-3-line text-xs" style={{ color: '#7c3aed' }} />
                  </span>
                  Backup Destination Path
                </label>
                <div className="flex items-center gap-2">
                  <span className="flex items-center justify-center w-9 h-10 rounded-xl bg-gray-50 border border-gray-200 flex-shrink-0">
                    <i className="ri-server-line text-sm text-gray-400" />
                  </span>
                  <input
                    type="text"
                    value={schedule.backup_destination}
                    onChange={(e) => updateSchedule({ backup_destination: e.target.value })}
                    placeholder="/var/backups/surveyerp"
                    disabled={!schedule.backup_enabled}
                    className="flex-1 border border-gray-200 rounded-xl px-3 py-2.5 text-sm font-mono focus:outline-none focus:ring-2 focus:ring-violet-500/30 focus:border-violet-400 disabled:opacity-50 disabled:bg-gray-50"
                  />
                </div>
                <p className="text-xs text-gray-400 mt-1">Absolute server path where .sql.gz files are stored</p>
              </div>

              {/* Compress toggle */}
              <div className="flex items-center justify-between p-4 rounded-xl border border-gray-100">
                <div className="flex items-center gap-3">
                  <span className="w-8 h-8 flex items-center justify-center rounded-lg bg-gray-50 flex-shrink-0">
                    <i className="ri-file-zip-line text-sm text-gray-500" />
                  </span>
                  <div>
                    <p className="text-sm font-semibold text-gray-800">Compress Backups (gzip)</p>
                    <p className="text-xs text-gray-500">Reduces file size by ~70% — recommended for large databases</p>
                  </div>
                </div>
                <button
                  type="button"
                  onClick={() => updateSchedule({ backup_compress: !schedule.backup_compress })}
                  disabled={!schedule.backup_enabled}
                  className={`relative w-12 h-6 rounded-full transition-all cursor-pointer flex-shrink-0 disabled:opacity-50 ${
                    schedule.backup_compress ? '' : 'bg-gray-200'
                  }`}
                  style={schedule.backup_compress ? { background: '#7c3aed' } : {}}
                >
                  <span
                    className="absolute top-0.5 w-5 h-5 bg-white rounded-full transition-all"
                    style={{ left: schedule.backup_compress ? '26px' : '2px' }}
                  />
                </button>
              </div>

              {/* Schedule summary */}
              {schedule.backup_enabled && (
                <div className="p-4 rounded-xl border border-violet-100 bg-violet-50/50">
                  <p className="text-xs font-semibold text-violet-700 mb-1 flex items-center gap-1.5">
                    <i className="ri-information-line" />
                    Current Schedule Summary
                  </p>
                  <p className="text-xs text-violet-600 leading-relaxed">
                    Backups run <strong>daily at {schedule.backup_time}</strong> IST ·
                    Stored at <code className="font-mono bg-violet-100 px-1 rounded">{schedule.backup_destination}</code> ·
                    Retained for <strong>{schedule.backup_retention_days} days</strong> ·
                    {schedule.backup_compress ? ' Compressed (gzip)' : ' Uncompressed'}
                    {schedule.backup_notify_email ? ` · Alerts → ${schedule.backup_notify_email}` : ''}
                  </p>
                </div>
              )}
            </div>
          )}
        </div>

        {/* ── API Diagnostics ──────────────────────────────────────────────── */}
        <ApiDiagnosticsPanel />

        {/* ── Database Connection Test ─────────────────────────────────────── */}
        <div className="bg-white rounded-xl border border-gray-100 overflow-hidden">
          <div className="px-5 py-4 border-b border-gray-100 flex items-center justify-between">
            <div>
              <h3 className="font-bold text-gray-900 text-sm">Database Connection Test</h3>
              <p className="text-xs text-gray-500 mt-0.5">Ping MariaDB and verify live connection details</p>
            </div>
            <button
              onClick={handleDbTest}
              disabled={dbTesting}
              className="flex items-center gap-2 px-4 py-2 text-white rounded-xl text-sm font-bold cursor-pointer transition-all whitespace-nowrap disabled:opacity-60"
              style={{ background: "#7c3aed" }}
            >
              {dbTesting ? (
                <><i className="ri-loader-4-line animate-spin" />Testing...</>
              ) : (
                <><i className="ri-wifi-line" />Test Connection</>
              )}
            </button>
          </div>

          <div className="p-5">
            {!dbResult && !dbTesting && (
              <div className="flex flex-col items-center justify-center py-8 text-gray-400">
                <span className="w-14 h-14 flex items-center justify-center rounded-2xl bg-gray-50 mb-3">
                  <i className="ri-database-2-line text-3xl text-gray-300" />
                </span>
                <p className="text-sm font-medium text-gray-500">Click "Test Connection" to ping MariaDB</p>
                <p className="text-xs text-gray-400 mt-1">Checks connectivity, latency, version, and table stats</p>
              </div>
            )}

            {dbTesting && (
              <div className="flex flex-col items-center justify-center py-8 text-gray-400">
                <i className="ri-loader-4-line text-3xl animate-spin mb-3" style={{ color: "#7c3aed" }} />
                <p className="text-sm font-medium text-gray-600">Connecting to MariaDB...</p>
              </div>
            )}

            {dbResult && !dbTesting && (
              <div className="space-y-4">
                {/* Status banner */}
                <div
                  className="flex items-center gap-3 p-4 rounded-xl border"
                  style={{
                    background: dbResult.status === 'connected' ? '#f0fdf4' : '#fef2f2',
                    borderColor: dbResult.status === 'connected' ? '#bbf7d0' : '#fecaca',
                  }}
                >
                  <span
                    className="w-10 h-10 flex items-center justify-center rounded-xl flex-shrink-0"
                    style={{ background: dbResult.status === 'connected' ? '#dcfce7' : '#fee2e2' }}
                  >
                    <i
                      className={`text-xl ${dbResult.status === 'connected' ? 'ri-checkbox-circle-fill' : 'ri-close-circle-fill'}`}
                      style={{ color: dbResult.status === 'connected' ? '#16a34a' : '#dc2626' }}
                    />
                  </span>
                  <div className="flex-1">
                    <p className="text-sm font-bold" style={{ color: dbResult.status === 'connected' ? '#15803d' : '#dc2626' }}>
                      {dbResult.status === 'connected' ? 'MariaDB Connected Successfully' : 'Connection Failed'}
                    </p>
                    <p className="text-xs mt-0.5" style={{ color: dbResult.status === 'connected' ? '#16a34a' : '#ef4444' }}>
                      {dbResult.status === 'connected'
                        ? `Response time: ${dbResult.latency_ms}ms · Tested at ${dbTestTime}`
                        : dbResult.error}
                    </p>
                  </div>
                  {dbResult.status === 'connected' && (
                    <span
                      className="text-xs font-bold px-3 py-1 rounded-full flex-shrink-0"
                      style={{
                        background: dbResult.latency_ms < 50 ? '#dcfce7' : dbResult.latency_ms < 200 ? '#fef9c3' : '#fee2e2',
                        color: dbResult.latency_ms < 50 ? '#15803d' : dbResult.latency_ms < 200 ? '#ca8a04' : '#dc2626',
                      }}
                    >
                      {dbResult.latency_ms}ms
                    </span>
                  )}
                </div>

                {dbResult.status === 'connected' && (
                  <>
                    {/* Connection details grid */}
                    <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                      {[
                        { label: "MariaDB Version", value: dbResult.db_version ?? "—", icon: "ri-database-2-line", color: "#7c3aed" },
                        { label: "Database Size", value: dbResult.db_size_mb ? `${dbResult.db_size_mb} MB` : "—", icon: "ri-hard-drive-2-line", color: "#10b981" },
                        { label: "Active Connections", value: dbResult.active_connections ?? "—", icon: "ri-links-line", color: "#f59e0b" },
                        { label: "Server Time", value: dbResult.server_time ? new Date(dbResult.server_time).toLocaleTimeString("en-IN", { hour: "2-digit", minute: "2-digit", second: "2-digit", hour12: true }) : "—", icon: "ri-time-line", color: "#3b82f6" },
                      ].map((item) => (
                        <div key={item.label} className="p-3 rounded-xl bg-gray-50 border border-gray-100">
                          <span className="w-7 h-7 flex items-center justify-center rounded-lg mb-2" style={{ background: `${item.color}15` }}>
                            <i className={`${item.icon} text-sm`} style={{ color: item.color }} />
                          </span>
                          <p className="text-sm font-bold text-gray-800 truncate">{item.value}</p>
                          <p className="text-xs text-gray-500 mt-0.5">{item.label}</p>
                        </div>
                      ))}
                    </div>

                    {/* Live table stats from DB */}
                    {dbResult.tables && dbResult.tables.length > 0 && (
                      <div>
                        <p className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-2">
                          Live Table Stats from MariaDB
                        </p>
                        <div className="rounded-xl border border-gray-100 overflow-hidden">
                          <table className="w-full">
                            <thead>
                              <tr className="bg-gray-50 border-b border-gray-100">
                                <th className="text-left px-4 py-2.5 text-xs font-semibold text-gray-500">Table</th>
                                <th className="text-right px-4 py-2.5 text-xs font-semibold text-gray-500">Rows</th>
                                <th className="text-right px-4 py-2.5 text-xs font-semibold text-gray-500">Size</th>
                              </tr>
                            </thead>
                            <tbody className="divide-y divide-gray-50">
                              {dbResult.tables.map((t) => (
                                <tr key={t.table_name} className="hover:bg-gray-50 transition-colors">
                                  <td className="px-4 py-2.5">
                                    <div className="flex items-center gap-2">
                                      <span className="w-5 h-5 flex items-center justify-center flex-shrink-0">
                                        <i className="ri-table-line text-xs text-gray-400" />
                                      </span>
                                      <span className="text-xs font-mono font-medium text-gray-800">{t.table_name}</span>
                                    </div>
                                  </td>
                                  <td className="px-4 py-2.5 text-right text-xs text-gray-600">
                                    {Number(t.table_rows).toLocaleString()}
                                  </td>
                                  <td className="px-4 py-2.5 text-right text-xs text-gray-600">
                                    {t.size_mb} MB
                                  </td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        </div>
                      </div>
                    )}
                  </>
                )}
              </div>
            )}
          </div>
        </div>

        {/* Database Tables */}
        <div className="bg-white rounded-xl border border-gray-100 overflow-hidden">
          <div className="px-5 py-4 border-b border-gray-100">
            <h3 className="font-bold text-gray-900 text-sm">Database Tables</h3>
            <p className="text-xs text-gray-500 mt-0.5">{tables.length} tables · {mockSystemStats.dbSize} total</p>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="border-b border-gray-100 bg-gray-50">
                  {["Table Name", "Rows", "Size", "Actions"].map((h) => (
                    <th key={h} className="text-left px-5 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wider">{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-50">
                {tables.map((t) => (
                  <tr key={t.name} className="hover:bg-gray-50 transition-all group">
                    <td className="px-5 py-3">
                      <div className="flex items-center gap-2">
                        <span className="w-6 h-6 flex items-center justify-center">
                          <i className="ri-table-line text-sm text-gray-400" />
                        </span>
                        <span className="text-sm font-mono font-medium text-gray-800">{t.name}</span>
                      </div>
                    </td>
                    <td className="px-5 py-3 text-sm text-gray-600">{t.rows.toLocaleString()}</td>
                    <td className="px-5 py-3 text-sm text-gray-600">{t.size}</td>
                    <td className="px-5 py-3">
                      <div className="flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                        <button onClick={() => showSuccess(`Optimizing table ${t.name}...`)}
                          className="text-xs px-2 py-1 rounded-lg border border-gray-200 text-gray-600 hover:bg-gray-100 cursor-pointer whitespace-nowrap">
                          Optimize
                        </button>
                        <button onClick={() => showSuccess(`Exporting ${t.name}...`)}
                          className="text-xs px-2 py-1 rounded-lg border border-gray-200 text-gray-600 hover:bg-gray-100 cursor-pointer whitespace-nowrap">
                          Export
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>

        {/* Backup History */}
        <div className="bg-white rounded-xl border border-gray-100 overflow-hidden">
          <div className="px-5 py-4 border-b border-gray-100">
            <h3 className="font-bold text-gray-900 text-sm">Backup History</h3>
          </div>
          <div className="divide-y divide-gray-50">
            {backups.map((b) => (
              <div key={b.id} className="flex items-center gap-4 px-5 py-3 hover:bg-gray-50 transition-all group">
                <span className="w-8 h-8 flex items-center justify-center rounded-xl flex-shrink-0" style={{ background: b.status === "Success" ? "#ecfdf5" : "#fef2f2" }}>
                  <i className={`${b.status === "Success" ? "ri-checkbox-circle-line text-amber-500" : "ri-close-circle-line text-red-500"} text-base`} />
                </span>
                <div className="flex-1 min-w-0">
                  <p className="text-xs font-mono font-medium text-gray-800 truncate">{b.name}</p>
                  <p className="text-xs text-gray-400 mt-0.5">{new Date(b.createdAt).toLocaleString("en-IN", { day: "numeric", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit", hour12: true })}</p>
                </div>
                <span className="text-xs text-gray-500 flex-shrink-0">{b.size}</span>
                <span className={`text-xs px-2 py-0.5 rounded-full font-medium flex-shrink-0 ${b.type === "Auto" ? "bg-gray-100 text-gray-500" : "bg-violet-50 text-violet-600"}`}>{b.type}</span>
                <div className="flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity flex-shrink-0">
                  <button onClick={() => showSuccess(`Downloading ${b.name}`)}
                    className="w-7 h-7 flex items-center justify-center rounded-lg hover:bg-gray-100 cursor-pointer text-gray-500">
                    <i className="ri-download-line text-sm" />
                  </button>
                  <button onClick={() => showSuccess(`Restoring from ${b.name}`)}
                    className="w-7 h-7 flex items-center justify-center rounded-lg hover:bg-violet-50 cursor-pointer" style={{ color: "#7c3aed" }}>
                    <i className="ri-restart-line text-sm" />
                  </button>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </ERPLayout>
  );
}
